import DateFnsUtils from "@date-io/date-fns";
import {
  Button,
  Container,
  Divider,
  Drawer,
  Typography,
} from "@material-ui/core";
import { ArrowDropDown } from "@material-ui/icons";
import Skeleton from "@material-ui/lab/Skeleton";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import dayjs from "dayjs";
import "dayjs/locale/id";
import { useRouter } from "next/router";
import React, { useContext, useEffect, useState } from "react";
import toast from "react-hot-toast";
import SwipeableViews from "react-swipeable-views";
import { autoPlay } from "react-swipeable-views-utils";
import { i18n, Router, withTranslation } from "../../../i18n";
import ImgIcon from "../../../public/img/IconImg";
import StarIcon from "../../../public/img/star";
import CardRoom from "../../../src/components/CardRoom";
import Cart from "../../../src/components/Cart";
import ErrorPage from "../../../src/components/ErrorPage";
import Navbar from "../../../src/components/Navbar";
import { CartRoomContext } from "../../../src/context/CartRoom";
import { HotelFacility } from "../../../src/libs/hotel";
import { getListHotels } from "../../../src/services/rooms";
import { getBuildDate } from "../../../src/utils/clearCache";
import nextDayDate from "../../../src/utils/nextDayDate";
import useStyles from "../../../styles/hotels/rooms";

const AutoPlaySwipeableViews = autoPlay(SwipeableViews);

type PropsHotelImage = {
  listImage: string[];
};

const HotelImage = (props: PropsHotelImage) => {
  const classes = useStyles(props);
  const { listImage } = props;
  const [indexImg, setIndexImg] = useState(0);

  return (
    <>
      <AutoPlaySwipeableViews
        index={indexImg}
        onChangeIndex={(index) => setIndexImg(index)}
      >
        {!listImage.length ? (
          <img
            src="/img/placeholder-banner.svg"
            alt="placeholder-banner"
            width="100%"
          />
        ) : (
          listImage.map((res, index) => (
            <img
              src={res}
              key={index}
              height={200}
              width="100%"
              alt="banner-detail-hotel"
              style={{ objectFit: "cover" }}
            />
          ))
        )}
      </AutoPlaySwipeableViews>
      {listImage?.length > 1 && (
        <>
          <div className={classes.paginationImg}>
            {listImage.slice(0, 10).map((res, index) => (
              <div
                key={index}
                className={index === indexImg ? classes.dotActive : classes.dot}
              />
            ))}
          </div>
          <div className={classes.imageIndex}>
            <ImgIcon />
            <Typography
              variant="caption"
              style={{ color: "#ffffff", paddingLeft: 5 }}
            >
              {indexImg + 1}/{listImage.length}
            </Typography>
          </div>
        </>
      )}
    </>
  );
};

interface ErrorDto {
  error_code: string;
  errors: string[];
  message: string;
  status_code: number;
}

interface DateCheckInType {
  in: Date | null;
  out: Date | null;
}

const DetailHotel = (props) => {
  const classes = useStyles(props);
  const [data, setData] = useState<HotelFacility>(Object);
  const { t } = props;
  const [loading, setLoading] = useState(true);
  const [version, setVersion] = useState("");
  const [error, setError] = useState<ErrorDto>(Object);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [dateCheckIn, setDateCheckIn] = useState<DateCheckInType>({
    in: new Date(),
    out: nextDayDate(new Date()),
  });
  const [prevDateCheckIn, setPrevDateCheckIn] = useState<DateCheckInType>({
    in: new Date(),
    out: nextDayDate(new Date()),
  });
  const [openCheckIn, setOpenCheckIn] = useState(false);
  const [openCheckOut, setOpenCheckOut] = useState(false);
  const [validDate, setValidDate] = useState(false);
  const {
    cart,
    setCart,
    cartOrderRoom,
    PlusOrderQty,
    MinOrderQty,
    settingCart,
    resetCart,
  } = useContext(CartRoomContext);
  const router = useRouter();
  const checkin = router.query.checkin as string;
  const checkout = router.query.checkout as string;
  const cityId = router.query.cityId as string;
  const hotelUsername = router.query.hotel_username as string;
  const language = i18n.language;
  const roomWithRatePlan = cart?.filter((room) => room.ratePlans.length !== 0);
  const unavailableRoom = cart?.filter((room) => room.qty === 0);

  const rangeNight = (first, end) => {
    const checkin = dayjs(first).format("YYYY-MM-DD");
    const checkout = dayjs(end).format("YYYY-MM-DD");
    return dayjs(checkout).diff(checkin, "days");
  };

  useEffect(() => {
    const version = parseInt(localStorage.getItem("version") || "0");
    if (version) {
      setVersion(getBuildDate(version));
    }
    const dataLocalStorage = [
      "selected_voucher",
      "promoCode",
      "dataProfile",
      "selectedPayment",
      "pointUsed",
      "firstAccessCheckout",
    ];
    dataLocalStorage.forEach((res) => {
      localStorage.removeItem(res);
    });
  }, []);

  const getDetailHotel = async () => {
    setLoading(true);
    try {
      const response = await getListHotels(
        version,
        cityId,
        JSON.stringify(dayjs(prevDateCheckIn.in).format("YYYY-MM-DD")),
        JSON.stringify(dayjs(prevDateCheckIn.out).format("YYYY-MM-DD")),
        hotelUsername
      );
      const data = response.data.filter(
        (val) => val.hotelUsername || val.hotelId === hotelUsername
      )[0];

      if (data) {
        setData(data);
        localStorage.setItem("hotel_data", JSON.stringify(data));
        const cart_order_room = JSON.parse(
          localStorage.getItem("cart_order_room") || "[]"
        );
        if (cart_order_room) {
          const allRoom = data?.rooms?.map(
            (obj) =>
              cart_order_room.find((o) => o.roomId === obj.roomId) || {
                ...obj,
                order: 0,
                ratePlans: obj.ratePlans.map((ratePlan) => {
                  return { ...ratePlan, order: 0 };
                }),
              }
          );
          setCart(allRoom);
        } else {
          setCart(settingCart(data.rooms));
        }
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setError(error?.response?.data?.meta);
    }
  };

  const handleValidateBackDate = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    const dateCheckin = new Date(checkin);
    const dateCheckout = new Date(checkout);

    const checkinDiff = new Date(dateCheckin).getTime() - yesterday.getTime();
    const checkoutDiff =
      new Date(dateCheckout).getTime() - dateCheckin.getTime();

    if (checkinDiff < 0 || checkoutDiff < 0) {
      if (cityId) {
        Router.push("/hotels/rooms");
      } else {
        Router.push(`/hotels/${hotelUsername}/select-date`);
      }
      toast.error("Date is invalid");
    } else {
      setValidDate(true);
    }
  };

  useEffect(() => {
    handleValidateBackDate();
    if (checkin && checkout) {
      const currentCheckin = new Date(checkin);
      const currentCheckout = new Date(checkout);
      setDateCheckIn({ in: currentCheckin, out: currentCheckout });
      setPrevDateCheckIn({
        in: currentCheckin,
        out: currentCheckout,
      });
    } else {
      router.replace({
        pathname: `/hotels/${hotelUsername}/rooms`,
        query: {
          checkin: dayjs(dateCheckIn.in).format("YYYY-MM-DD"),
          checkout: dayjs(dateCheckIn.out).format("YYYY-MM-DD"),
          authToken: router.query.autToken,
        },
      });
    }
  }, [router.query]);

  const handleSearch = () => {
    localStorage.removeItem("cart_order_room");
    setPrevDateCheckIn(dateCheckIn);
    setOpenDrawer(false);
    router.replace({
      pathname: `/hotels/${hotelUsername}/rooms`,
      query: {
        checkin: dayjs(dateCheckIn.in).format("YYYY-MM-DD"),
        checkout: dayjs(dateCheckIn.out).format("YYYY-MM-DD"),
        authToken: router.query.authToken,
      },
    });
  };

  useEffect(() => {
    if (checkin && checkout && version && validDate) {
      getDetailHotel();
      localStorage.setItem(
        "dateCheckIn",
        JSON.stringify({
          checkin: checkin,
          checkout: checkout,
        })
      );
    }
  }, [checkin, checkout, version, validDate]);

  const handleBackNavbar = () => {
    resetCart();
    localStorage.removeItem("cart_order_room");
    if (cityId) {
      Router.push(
        `/hotels/rooms/search-rooms?cityId=${cityId}&checkin=${checkin}&checkout=${checkout}`
      );
    } else {
      Router.push(`/hotels/${hotelUsername}/select-date`);
    }
  };

  return (
    <div>
      {!loading && (error?.message || !data.hotelId) ? (
        <ErrorPage
          error={error?.status_code || 400}
          errorMessage={
            error?.message || "Room Feature on this hotel was disabled!"
          }
          t={t}
        />
      ) : (
        <Container maxWidth="xs" className={classes.root}>
          <Navbar
            t={t}
            title={data?.hotelName}
            onClickBack={handleBackNavbar}
          />
          {loading ? (
            <SkeletonLoading />
          ) : (
            <div style={{ backgroundColor: "#ffffff" }}>
              <div style={{ position: "relative" }}>
                <HotelImage listImage={data.hotelImageUrls} />
              </div>

              <div className={classes.detailWrapper}>
                <Typography variant="caption" className={classes.title}>
                  {data?.hotelName}
                </Typography>
                <div>
                  <Typography variant="caption" className={classes.address}>
                    {data?.hotelLocation}
                  </Typography>
                </div>

                <div className={classes.rateHotel}>
                  <StarIcon />
                  <Typography variant="caption" className={classes.rateText}>
                    {(i18n.language === "en" ? `${data.hotelRating} ` : "") +
                      t("hotel-star") +
                      (i18n.language === "id" ? ` ${data.hotelRating}` : "")}
                  </Typography>
                </div>
              </div>

              <Divider style={{ backgroundColor: "#e8e8e8" }} />
            </div>
          )}
          {!cart.length && (
            <Divider
              style={{
                height: 8,
                backgroundColor: "#E8E8E8",
                opacity: 0.5,
              }}
            />
          )}
          <div
            onClick={() => setOpenDrawer(!openDrawer)}
            className={classes.displayDate}
          >
            <div style={{ padding: 8 }}>
              <Typography>
                {dayjs(prevDateCheckIn.in)
                  .locale(language)
                  .format("DD MMMM YYYY")}{" "}
                -{" "}
                {dayjs(prevDateCheckIn.out)
                  .locale(language)
                  .format("DD MMMM YYYY")}
              </Typography>
            </div>
            <div
              style={{
                backgroundColor: "#9e007e",
                height: 40,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 40,
              }}
            >
              <ArrowDropDown style={{ color: "white" }} />
            </div>
          </div>
          <div
            className={classes.listRoomWrapper}
            style={{ paddingBottom: cartOrderRoom.totalRoom !== 0 ? 90 : 16 }}
          >
            {cart.length !== 0 &&
              roomWithRatePlan.map((res, index) => {
                const selectedRoomTypeIndex = cart.findIndex(
                  (val) => val.roomId === res.roomId
                );
                const selectedRoomType = cart[selectedRoomTypeIndex];

                return (
                  <div
                    key={res.roomId}
                    style={{ paddingTop: index !== 0 ? 16 : 0 }}
                  >
                    <CardRoom
                      skeleton={loading ? true : false}
                      isCanAdd={res.qty}
                      quantity={selectedRoomType.order}
                      PlusOrderQty={PlusOrderQty}
                      MinOrderQty={MinOrderQty}
                      data={res}
                      t={t}
                    />
                  </div>
                );
              })}
            {!loading &&
              (!cart.length ||
                !roomWithRatePlan.length ||
                unavailableRoom.length === cart.length) && (
                <div style={{ textAlign: "center" }}>
                  <img src="/img/empty-room.svg" alt="empty-room" />
                  <Typography style={{ marginTop: 16, fontSize: 14 }}>
                    {t("no-rooms-available-at-this-hotel")}
                  </Typography>
                </div>
              )}
          </div>
          {cartOrderRoom.totalRoom !== 0 && (
            <Cart
              totalQty={cartOrderRoom.totalRoom}
              totalPrice={cartOrderRoom.totalPrice}
              hotel={cartOrderRoom.hotelName}
              t={t}
            />
          )}
          <Drawer
            anchor="top"
            open={openDrawer}
            onClose={() => {
              setOpenDrawer(false);
              setDateCheckIn(prevDateCheckIn);
            }}
            className={classes.drawerDatePicker}
          >
            <div>
              <Typography variant="body2" className={classes.titleCard}>
                Check-In
              </Typography>
              <div style={{ display: "flex", position: "relative" }}>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <DatePicker
                    value={dateCheckIn.in}
                    onChange={(date) =>
                      setDateCheckIn({
                        ...dateCheckIn,
                        in: date,
                        out: nextDayDate(date),
                      })
                    }
                    animateYearScrolling
                    minDate={new Date()}
                    open={openCheckIn}
                    onClose={() => setOpenCheckIn(false)}
                    TextFieldComponent={() => (
                      <div
                        className={classes.areaSearch}
                        onClick={() => setOpenCheckIn(true)}
                      >
                        <div style={{ marginRight: 8 }}>
                          <img
                            src="/img/checkin-icon.svg"
                            className={classes.calendarIcon}
                            alt="checkin"
                          />
                        </div>
                        <div className={classes.searchLabel}>
                          <div>
                            <Typography className={classes.bold}>
                              {dayjs(dateCheckIn.in)
                                .locale(language)
                                .format("dddd, D MMMM YYYY")}
                            </Typography>
                          </div>
                        </div>
                      </div>
                    )}
                  />
                </MuiPickersUtilsProvider>
                <div className={classes.totalNight}>
                  <img
                    src="/img/moonlight.svg"
                    alt="moon"
                    style={{ marginRight: 4 }}
                  />
                  <Typography variant="caption">
                    {rangeNight(dateCheckIn.in, dateCheckIn.out)}
                  </Typography>
                  <div>
                    <Typography variant="caption">
                      {language === "en" &&
                      rangeNight(dateCheckIn.in, dateCheckIn.out) > 1
                        ? t("nights")
                        : t("night")}
                    </Typography>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <Typography variant="body2" className={classes.titleCard}>
                Check-Out
              </Typography>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <DatePicker
                  value={dateCheckIn.out}
                  onChange={(date) =>
                    setDateCheckIn({ ...dateCheckIn, out: date })
                  }
                  animateYearScrolling
                  open={openCheckOut}
                  minDate={nextDayDate(dateCheckIn.in)}
                  onClose={() => setOpenCheckOut(false)}
                  TextFieldComponent={() => (
                    <div
                      className={classes.areaSearch}
                      onClick={() => setOpenCheckOut(true)}
                    >
                      <div style={{ marginRight: 8 }}>
                        <img
                          src="/img/checkout-icon.svg"
                          className={classes.calendarIcon}
                          alt="checkout"
                        />
                      </div>
                      <div className={classes.searchLabel}>
                        <div>
                          <Typography className={classes.bold}>
                            {dayjs(dateCheckIn.out)
                              .locale(language)
                              .format("dddd, D MMMM YYYY")}
                          </Typography>
                        </div>
                      </div>
                    </div>
                  )}
                />
              </MuiPickersUtilsProvider>
            </div>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              className={classes.buttonSearch}
              onClick={handleSearch}
            >
              {t("find-rooms")}
            </Button>
          </Drawer>
        </Container>
      )}
    </div>
  );
};

DetailHotel.getInitialProps = async () => ({
  namespacesRequired: ["common"],
});

export default withTranslation("common")(DetailHotel);

function SkeletonLoading(props) {
  const classes = useStyles(props);

  return (
    <>
      <Skeleton variant="rect" height={200} />

      <div className={classes.detailWrapper}>
        <Skeleton variant="text" width={160} />
        <Skeleton variant="text" width={130} />

        <div className={classes.rateHotel}>
          <Skeleton variant="text" width={120} />
        </div>
      </div>

      <Divider style={{ height: 0.5 }} />
    </>
  );
}
